// 📦 src/context/CartContext.jsx
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getCart, createCart, addToCart as shopifyAddToCart, updateCartItem as shopifyUpdateCartItem, removeCartItem as shopifyRemoveCartItem } from "../utils/shopify";

const CART_ID_KEY = "shopify_cart_id";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calculate count from cart
  const count = cart?.lines?.edges?.reduce((sum, edge) => sum + (edge?.node?.quantity || 0), 0) || 0;

  // ✅ Fetch cart by ID from localStorage
  const fetchCart = useCallback(async () => {
    const cartId = localStorage.getItem(CART_ID_KEY);
    if (!cartId) {
      setCart(null);
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      const fetchedCart = await getCart(cartId);
      
      // ✅ Persist cart ID if not already stored
      if (fetchedCart?.id) {
        localStorage.setItem(CART_ID_KEY, fetchedCart.id);
      }
      
      setCart(fetchedCart);
      return fetchedCart;
    } catch (err) {
      console.error("❌ Failed to fetch cart:", err);
      setError(err.message);
      // If cart doesn't exist, clear localStorage
      localStorage.removeItem(CART_ID_KEY);
      setCart(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Add item to cart
  const addToCart = async (variantId, quantity = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      let cartId = localStorage.getItem(CART_ID_KEY);
      
      // Create cart if it doesn't exist
      if (!cartId) {
        const newCart = await createCart();
        cartId = newCart.id;
        localStorage.setItem(CART_ID_KEY, cartId);
        setCart(newCart);
      }
      
      // Add item to cart
      const updatedCart = await shopifyAddToCart(cartId, variantId, quantity);
      
      // ✅ Persist cart ID
      if (updatedCart?.id) {
        localStorage.setItem(CART_ID_KEY, updatedCart.id);
      }
      
      // ✅ Update state immediately for React re-render
      setCart(updatedCart);
      
      return updatedCart;
    } catch (err) {
      console.error("❌ Failed to add to cart:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update cart item quantity
  const updateCartItem = async (lineId, quantity) => {
    try {
      setLoading(true);
      setError(null);
      
      const cartId = localStorage.getItem(CART_ID_KEY);
      if (!cartId) {
        throw new Error("No cart found");
      }
      
      const updatedCart = await shopifyUpdateCartItem(cartId, lineId, quantity);
      
      // ✅ Persist cart ID
      if (updatedCart?.id) {
        localStorage.setItem(CART_ID_KEY, updatedCart.id);
      }
      
      // ✅ Update state immediately for React re-render
      setCart(updatedCart);
      
      return updatedCart;
    } catch (err) {
      console.error("❌ Failed to update cart item:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ✅ Remove item from cart
  const removeCartItem = async (lineId) => {
    try {
      setLoading(true);
      setError(null);
      
      const cartId = localStorage.getItem(CART_ID_KEY);
      if (!cartId) {
        throw new Error("No cart found");
      }
      
      const updatedCart = await shopifyRemoveCartItem(cartId, lineId);
      
      // ✅ Persist cart ID
      if (updatedCart?.id) {
        localStorage.setItem(CART_ID_KEY, updatedCart.id);
      }
      
      // ✅ Update state immediately for React re-render
      setCart(updatedCart);
      
      return updatedCart;
    } catch (err) {
      console.error("❌ Failed to remove cart item:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ✅ Clear cart
  const clearCart = () => {
    localStorage.removeItem(CART_ID_KEY);
    setCart(null);
    setError(null);
  };

  // ✅ Refresh cart (re-fetch by ID)
  const refresh = async () => {
    return await fetchCart();
  };

  // ✅ Fetch cart on mount if cartId exists
  useEffect(() => {
    const cartId = localStorage.getItem(CART_ID_KEY);
    if (cartId) {
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [fetchCart]);

  const value = {
    cart,
    count,
    loading,
    error,
    addToCart,
    updateCartItem,
    removeCartItem,
    clearCart,
    refresh,
    fetchCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
};
